/**
 * ISO 4217 currency codes supported by the app, paired with the number of
 * decimal places that define a "minor unit" for that currency.
 *
 * This table is the single source of truth for money math. Consumers MUST
 * NOT hardcode `2` for decimal places — always read {@link getCurrencyScale}.
 *
 * Scales follow ISO 4217:
 *  - scale 0 : JPY, KRW, HUF, CLP, VND, ...
 *  - scale 2 : USD, EUR, GBP, ... (the majority)
 *  - scale 3 : BHD, KWD, OMR, TND, JOD (Middle East dinars)
 */

export type KnownCurrencyCode =
  | 'USD'
  | 'EUR'
  | 'GBP'
  | 'CAD'
  | 'AUD'
  | 'JPY'
  | 'CNY'
  | 'INR'
  | 'KRW'
  | 'BRL'
  | 'MXN'
  | 'CHF'
  | 'SEK'
  | 'NOK'
  | 'DKK'
  | 'PLN'
  | 'CZK'
  | 'HUF'
  | 'RUB'
  | 'TRY'
  | 'ZAR'
  | 'SGD'
  | 'HKD'
  | 'NZD'
  | 'THB'
  | 'BHD'
  | 'KWD'
  | 'OMR';

export type CurrencyCode = string;

export interface CurrencyInfo {
  readonly code: CurrencyCode;
  readonly scale: number;
  readonly symbol: string;
  readonly name: string;
  /** Preferred default placement for the symbol when a locale-aware format is unavailable. */
  readonly symbolPosition: 'before' | 'after';
}

const REGISTRY: Record<KnownCurrencyCode, CurrencyInfo> = {
  USD: { code: 'USD', scale: 2, symbol: '$', name: 'US Dollar', symbolPosition: 'before' },
  EUR: { code: 'EUR', scale: 2, symbol: '€', name: 'Euro', symbolPosition: 'before' },
  GBP: { code: 'GBP', scale: 2, symbol: '£', name: 'British Pound', symbolPosition: 'before' },
  CAD: { code: 'CAD', scale: 2, symbol: 'C$', name: 'Canadian Dollar', symbolPosition: 'before' },
  AUD: { code: 'AUD', scale: 2, symbol: 'A$', name: 'Australian Dollar', symbolPosition: 'before' },
  JPY: { code: 'JPY', scale: 0, symbol: '¥', name: 'Japanese Yen', symbolPosition: 'before' },
  CNY: { code: 'CNY', scale: 2, symbol: '¥', name: 'Chinese Yuan', symbolPosition: 'before' },
  INR: { code: 'INR', scale: 2, symbol: '₹', name: 'Indian Rupee', symbolPosition: 'before' },
  KRW: { code: 'KRW', scale: 0, symbol: '₩', name: 'South Korean Won', symbolPosition: 'before' },
  BRL: { code: 'BRL', scale: 2, symbol: 'R$', name: 'Brazilian Real', symbolPosition: 'before' },
  MXN: { code: 'MXN', scale: 2, symbol: '$', name: 'Mexican Peso', symbolPosition: 'before' },
  CHF: { code: 'CHF', scale: 2, symbol: 'CHF', name: 'Swiss Franc', symbolPosition: 'before' },
  SEK: { code: 'SEK', scale: 2, symbol: 'kr', name: 'Swedish Krona', symbolPosition: 'after' },
  NOK: { code: 'NOK', scale: 2, symbol: 'kr', name: 'Norwegian Krone', symbolPosition: 'after' },
  DKK: { code: 'DKK', scale: 2, symbol: 'kr', name: 'Danish Krone', symbolPosition: 'after' },
  PLN: { code: 'PLN', scale: 2, symbol: 'zł', name: 'Polish Złoty', symbolPosition: 'after' },
  CZK: { code: 'CZK', scale: 2, symbol: 'Kč', name: 'Czech Koruna', symbolPosition: 'after' },
  HUF: { code: 'HUF', scale: 0, symbol: 'Ft', name: 'Hungarian Forint', symbolPosition: 'after' },
  RUB: { code: 'RUB', scale: 2, symbol: '₽', name: 'Russian Ruble', symbolPosition: 'after' },
  TRY: { code: 'TRY', scale: 2, symbol: '₺', name: 'Turkish Lira', symbolPosition: 'before' },
  ZAR: { code: 'ZAR', scale: 2, symbol: 'R', name: 'South African Rand', symbolPosition: 'before' },
  SGD: { code: 'SGD', scale: 2, symbol: 'S$', name: 'Singapore Dollar', symbolPosition: 'before' },
  HKD: { code: 'HKD', scale: 2, symbol: 'HK$', name: 'Hong Kong Dollar', symbolPosition: 'before' },
  NZD: { code: 'NZD', scale: 2, symbol: 'NZ$', name: 'New Zealand Dollar', symbolPosition: 'before' },
  THB: { code: 'THB', scale: 2, symbol: '฿', name: 'Thai Baht', symbolPosition: 'before' },
  BHD: { code: 'BHD', scale: 3, symbol: '.د.ب', name: 'Bahraini Dinar', symbolPosition: 'before' },
  KWD: { code: 'KWD', scale: 3, symbol: 'د.ك', name: 'Kuwaiti Dinar', symbolPosition: 'before' },
  OMR: { code: 'OMR', scale: 3, symbol: 'ر.ع.', name: 'Omani Rial', symbolPosition: 'before' },
};

export const CURRENCIES: readonly CurrencyInfo[] = Object.freeze(
  (Object.keys(REGISTRY) as KnownCurrencyCode[]).map((code) => Object.freeze(REGISTRY[code])),
);

const CODE_SET = new Set<string>(Object.keys(REGISTRY));
const CUSTOM_CODE_RE = /^[A-Z]{1,3}$/u;

export function isCurrencyCode(value: string): value is CurrencyCode {
  return CUSTOM_CODE_RE.test(value);
}

export function isKnownCurrencyCode(value: string): value is KnownCurrencyCode {
  return CODE_SET.has(value);
}

export function getCurrency(code: CurrencyCode): CurrencyInfo {
  if (isKnownCurrencyCode(code)) {
    return REGISTRY[code];
  }
  if (!isCurrencyCode(code)) {
    throw new Error(`Unknown currency code: ${String(code)}`);
  }
  return Object.freeze({
    code,
    scale: 2,
    symbol: code,
    name: code,
    symbolPosition: 'before' as const,
  });
}

export function getCurrencyScale(code: CurrencyCode): number {
  return getCurrency(code).scale;
}
